// Homepage registry: all page content data extracted from App.tsx inline JSX.
// Layout and style decisions stay in pattern components; only copy and config live here.

// ---------------------------------------------------------------------------
// Hero — OPC narrative pivot (2026-04-17)
// ---------------------------------------------------------------------------

export type HeroBadge = {
  text: string;
  bgClass: string;
  hoverBgClass: string;
  hoverTextClass: string;
};

export type HeroCTA = {
  label: string;
  href: string;
  variant: "default" | "secondary" | "dark";
  showArrow: boolean;
};

export const heroContent = {
  identityBrand: "CALL ANYTHING",
  identityBrandCN: "一呼百应",
  badges: [
    {
      text: "OPEN PROTOCOL · LOCAL-FIRST RUNTIME",
      bgClass: "bg-white",
      hoverBgClass: "hover:bg-[#3B82F6]",
      hoverTextClass: "hover:text-white",
    },
    {
      text: "APACHE-2.0 OPEN SOURCE",
      bgClass: "bg-[#A3E635]",
      hoverBgClass: "hover:bg-[#6366F1]",
      hoverTextClass: "hover:text-white",
    },
  ] as HeroBadge[],
  statementLines: ["私有能力，公开契约，", "Agent 直接调用。"],
  subHeadline:
    "把你的本地脚本、工作流、模型或专业判断封装成一根 Hotline。实现留在你这里；Agent 通过统一协议发现、调用，并读取标准 result_package。",
  bodyParagraph: [
    "你公开的是契约，不是源码。Agent 学会调用契约，而不是窥探实现。",
    "CALL ANYTHING 是 Agent 调用外部能力的开放协议与本地运行时，不是电话工具，也不是传统通信 SaaS。",
  ],
  primaryCTA: {
    label: "创建我的第一根 Hotline",
    href: "/docs/quick-start-responder",
    variant: "default" as const,
    showArrow: true,
  } satisfies HeroCTA,
  secondaryCTA: {
    label: "在 Agent 里调用 Hotline",
    href: "/docs/quick-start-caller",
    variant: "secondary" as const,
    showArrow: false,
  } satisfies HeroCTA,
  tertiaryLink: {
    label: "查看自托管方案",
    href: "/docs/protocol-architecture",
  },
};

// ---------------------------------------------------------------------------
// Architecture (kept — only used in S11 FOR ENGINEERS disclosure)
// ---------------------------------------------------------------------------

export type ArchitecturePillar = {
  title: string;
  /** Matches Playground F.2 product layer icons in `src/app/components/icons.tsx` */
  productIcon: "Protocol" | "Client" | "Platform" | "Selfhost";
  bg: string;
  hoverBg: string;
  text?: string;
  subtitle: string;
  desc: string;
};

export const architecturePillars: ArchitecturePillar[] = [
  {
    title: "PROTOCOL",
    productIcon: "Protocol",
    bg: "bg-[#A3E635]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#A3E635]/95",
    subtitle: "协议层 · 真相源",
    desc: "定义合约形状、token 模型、状态机与 transport 抽象。发布为 @delexec/contracts，所有组件遵守同一份真相源",
  },
  {
    title: "CLIENT",
    productIcon: "Client",
    bg: "bg-[#3B82F6]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#3B82F6]/95",
    text: "text-white",
    subtitle: "用户本地进程组",
    desc: "Caller Controller 发起调用、Responder Controller 暴露 Hotline、Ops Supervisor 编排进程。一台机器可只跑一边、也可同机两边",
  },
  {
    title: "PLATFORM",
    productIcon: "Platform",
    bg: "bg-[#8B5CF6]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#8B5CF6]/95",
    text: "text-white",
    subtitle: "控制面服务",
    desc: "catalog · token · delivery-meta · metrics。官方托管的公共控制面，不在数据面 hop 业务流量",
  },
  {
    title: "SELFHOST",
    productIcon: "Selfhost",
    bg: "bg-[#FACC15]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#FACC15]/95",
    text: "text-black",
    subtitle: "Platform 自托管形态",
    desc: "同一份控制面服务的两种部署方式：用公有 Platform、或在企业内自托管 Selfhost，能力对等",
  },
];

// ---------------------------------------------------------------------------
// S2 · Status Quo · 当前真实状态
// ---------------------------------------------------------------------------

export type StatusQuoCard = {
  num: string;
  title: string;
  body: string;
  bg: string;
  hoverBg: string;
  text: "text-black" | "text-white";
  status: "available" | "preview" | "planned";
};

/** @deprecated Use StatusQuoCard instead */
export type StatusQuoPainCard = StatusQuoCard;

export const statusQuoCards: StatusQuoCard[] = [
  {
    num: "01",
    title: "Local Mode",
    body: "本地安装即可跑通，无需公网平台。",
    bg: "bg-[#A3E635]",
    hoverBg: "hover:brightness-95",
    text: "text-black",
    status: "available",
  },
  {
    num: "02",
    title: "@delexec/ops CLI",
    body: "用于 Caller / Responder 本地运行时。",
    bg: "bg-[#3B82F6]",
    hoverBg: "hover:brightness-95",
    text: "text-white",
    status: "available",
  },
  {
    num: "03",
    title: "Self-host Platform",
    body: "用于团队或组织自建 catalog、review 和 routing。",
    bg: "bg-[#8B5CF6]",
    hoverBg: "hover:brightness-95",
    text: "text-white",
    status: "available",
  },
  {
    num: "04",
    title: "Public Marketplace",
    body: "当前展示示例 Hotline，用于说明 catalog 和 contract 结构。",
    bg: "bg-[#FACC15]",
    hoverBg: "hover:brightness-95",
    text: "text-black",
    status: "preview",
  },
  {
    num: "05",
    title: "Fiat Settlement",
    body: "法币结算与提现机制，规划中。",
    bg: "bg-[#EC4899]",
    hoverBg: "hover:brightness-95",
    text: "text-white",
    status: "planned",
  },
];

/** @deprecated Use statusQuoCards instead */
export const statusQuoPainPoints = statusQuoCards;

// ---------------------------------------------------------------------------
// S3 · Hotline ≠ API / MCP / Skill
// ---------------------------------------------------------------------------

export const hotlineKitchenAnalogy = {
  left: {
    emoji: "🔌",
    title: "接入面：API / MCP / Skills",
    sub: "解决「Agent 如何调用工具和服务」的问题。",
    bullets: [
      "API 提供稳定的服务接口",
      "MCP 让 Agent 统一调用工具",
      "Skills 给模型注入行为指导",
    ],
  },
  right: {
    emoji: "📦",
    title: "产品化层：Hotline",
    sub: "解决「能力如何被发现、版本化、计量和治理」的问题。",
    bullets: [
      "一根 Hotline 可以使用 API、MCP 或 Skill 作为接入面",
      "CALL ANYTHING 标准化的是能力本体",
      "契约公开，实现私有",
    ],
  },
};

export type HotlineComparisonRow = {
  dimension: string;
  api: string;
  mcp: string;
  skill: string;
  hotline: string;
};

export const hotlineComparisonRows: HotlineComparisonRow[] = [
  {
    dimension: "解决的问题",
    api: "服务接口：Agent 如何调用外部服务",
    mcp: "工具调用：Agent runtime 如何 invoke 工具",
    skill: "行为注入：Agent 如何获得行为指导和工作流",
    hotline: "能力产品化：私有能力如何被发现、调用、版本化、计量和治理",
  },
  {
    dimension: "供给方",
    api: "公司 / 平台",
    mcp: "工具开发者",
    skill: "Skill 作者",
    hotline: "个人或小团队（超级个体 / OPC）",
  },
  {
    dimension: "消费方",
    api: "应用开发者",
    mcp: "Agent runtime",
    skill: "Agent 上下文",
    hotline: "任何符合协议的 Agent",
  },
  {
    dimension: "集成模式",
    api: "调用方编写 client code",
    mcp: "Agent runtime 启动 server",
    skill: "SKILL.md 注入 prompt",
    hotline: "两端遵循同一份契约，无需相互集成",
  },
  {
    dimension: "身份与鉴权",
    api: "由服务方自行实现",
    mcp: "跟随 Agent 运行时（无协议级标准）",
    skill: "通常无身份层",
    hotline: "协议层 token + introspect",
  },
  {
    dimension: "计量与结算",
    api: "由服务方接入支付系统",
    mcp: "通常无内置计量",
    skill: "通常无商业化机制",
    hotline: "协议字段自带定价 / 用量 / 回执",
  },
  {
    dimension: "可发现性",
    api: "由服务方自行营销",
    mcp: "随客户端预装或手动配置",
    skill: "复制文件至本地",
    hotline: "Marketplace catalog + 评分 + 版本",
  },
  {
    dimension: "隐私边界",
    api: "由实现方决定",
    mcp: "协议不暴露实现细节",
    skill: "内容完全进入模型上下文",
    hotline: "核心实现留在 Responder，对外仅暴露契约",
  },
  {
    dimension: "兼容关系",
    api: "Hotline 可以暴露为 OpenAPI endpoint",
    mcp: "Hotline 可以暴露为 MCP Server",
    skill: "Hotline 可以包装为 Skill wrapper",
    hotline: "能力产品化契约，接入面可以是 API / MCP / Skill",
  },
];

// ---------------------------------------------------------------------------
// S4 · Operator Personas（替代旧 scenarioCards）
// ---------------------------------------------------------------------------

export type OperatorPersona = {
  num: string;
  role: string;
  before: string;
  after: string;
  bg: string;
  hoverBg: string;
  text: "text-black" | "text-white";
};

export const operatorPersonas: OperatorPersona[] = [
  {
    num: "01",
    role: "创意方向 · 独立模型作者",
    before: "在社区发布模型，靠零散打赏",
    after: "开一根创意生成 Hotline，每次调用即时结算",
    bg: "bg-[#14B8A6]/80 backdrop-blur-sm",
    hoverBg: "hover:bg-[#14B8A6]/95",
    text: "text-white",
  },
  {
    num: "02",
    role: "垂类专家 · 独立顾问",
    before: "一对一咨询，时薪有上限",
    after: "将领域 know-how 封装进 Hotline，数字分身 7×24 承接调用",
    bg: "bg-[#F97316]/80 backdrop-blur-sm",
    hoverBg: "hover:bg-[#F97316]/95",
    text: "text-white",
  },
  {
    num: "03",
    role: "生产服务 · 独立创作工作室",
    before: "客户排队等待交付",
    after: "Hotline 自动承接订单，本地工作站夜间持续跑作业",
    bg: "bg-[#EC4899]/80 backdrop-blur-sm",
    hoverBg: "hover:bg-[#EC4899]/95",
    text: "text-white",
  },
  {
    num: "04",
    role: "「你」· 任何只有你会的事",
    before: "经验仅存在于个人脑中 / 硬盘里",
    after: "上架为 Hotline，让全球 Agent 知道在哪里能调用到这件事",
    bg: "bg-[#8B5CF6]/80 backdrop-blur-sm",
    hoverBg: "hover:bg-[#8B5CF6]/95",
    text: "text-white",
  },
];

export type EvolutionStep = {
  stage: string;
  meta: string;
};

export const operatorEvolutionSteps: EvolutionStep[] = [
  { stage: "一根 Hotline", meta: "一个人" },
  { stage: "被反复调用", meta: "月活活跃" },
  { stage: "盈利覆盖运维", meta: "盈亏平衡" },
  { stage: "组队升级", meta: "团队 2-3 人" },
  { stage: "公司化稳定服务节点", meta: "公司" },
];

// ---------------------------------------------------------------------------
// S5 · Protocol Benefits（替代旧 coreConceptStandardizedPoints）
// ---------------------------------------------------------------------------

export type ProtocolBenefitCard = {
  num: string;
  engineering: string;
  business: string;
  detail: string;
  bg: string;
  hoverBg: string;
};

export const protocolBenefits: ProtocolBenefitCard[] = [
  {
    num: "01",
    engineering: "身份 → 协议层 token + introspect",
    business: "无需自建登录系统",
    detail: "协议层 token 让任何 Agent 都能合法寻址",
    bg: "bg-[#A3E635]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#A3E635]/95",
  },
  {
    num: "02",
    engineering: "计费 → 协议字段自带定价 / 用量 / 结算回执",
    business: "无需接入 Stripe，无需自建账单页",
    detail: "每次调用自带回执，由平台统一对账",
    bg: "bg-[#3B82F6]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#3B82F6]/95",
  },
  {
    num: "03",
    engineering: "审批 → Caller 端 Console 内置审批",
    business: "无需自写风控",
    detail: "Caller 端 Console 代为完成拦截与确认",
    bg: "bg-[#8B5CF6]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#8B5CF6]/95",
  },
  {
    num: "04",
    engineering: "可观测 → result_package",
    business: "无需自建 admin 后台",
    detail: "调用记录 / 用量 / 错误率全部标准化",
    bg: "bg-[#F97316]/75 backdrop-blur-sm",
    hoverBg: "hover:bg-[#F97316]/95",
  },
  {
    num: "05",
    engineering: "路由 → Hotline ID 内含版本",
    business: "无需自建发布 / 灰度 / 多版本管理",
    detail: "Hotline ID 即是路由键",
    bg: "bg-[#FACC15]/85 backdrop-blur-sm",
    hoverBg: "hover:bg-[#FACC15]/95",
  },
];

export type ResponderConsoleMockupCard = {
  label: string;
  value: string;
  delta: string;
  tone: "green" | "blue" | "purple" | "orange" | "yellow";
};

export const responderConsoleMockupCards: ResponderConsoleMockupCard[] = [
  { label: "调用追踪", value: "result_package", delta: "标准化记录", tone: "green" },
  { label: "版本管理", value: "contract v1", delta: "schema + examples", tone: "blue" },
  { label: "计量审计", value: "per-call", delta: "协议层自动", tone: "purple" },
  { label: "状态监控", value: "online", delta: "本地 supervisor", tone: "orange" },
  { label: "注册草案", value: "draft", delta: "可提交 review", tone: "yellow" },
];

// ---------------------------------------------------------------------------
// S6 · Subcontract Topology
// ---------------------------------------------------------------------------

export type SubcontractBullet = {
  title: string;
  body: string;
};

export const subcontractBullets: SubcontractBullet[] = [
  {
    title: "你不必所有事都自己做",
    body: "整合本身就是产品。别人专注于垂类供给，你负责把它们组合成场景。",
  },
  {
    title: "整张网络自动复合",
    body: "你赚取整合差价，上游赚取专业溢价，调用计费由协议层自动结算。",
  },
  {
    title: "人类公司怎么分包，Hotline 网络就怎么分包",
    body: "你随时可以把自己负责的某一段抽出来再外包给别的 Hotline，链路自动级联。",
  },
];

// ---------------------------------------------------------------------------
// S7 · Marketplace Metrics Bar (顶部 placeholder)
// ---------------------------------------------------------------------------

export const marketplaceMetricsBar = {
  status: "PREVIEW · 示例 Hotline 目录",
  metrics: [
    { value: "Preview", label: "Marketplace 状态", note: "当前展示示例 Hotline" },
    { value: "示例", label: "Hotline 类型", note: "用于说明 catalog 和 contract 结构" },
    { value: "开放", label: "协议", note: "Apache-2.0 开源" },
  ],
};

// ---------------------------------------------------------------------------
// S8 · Objections Q&A
// ---------------------------------------------------------------------------

export type ObjectionCard = {
  num: string;
  question: string;
  bodyLines: string[];
  accent: "green" | "yellow" | "pink";
};

export const objectionsQA: ObjectionCard[] = [
  {
    num: "Q1",
    question: "Hotline 和 MCP 是什么关系？",
    bodyLines: [
      "MCP 处理 Agent runtime 如何调用工具——它是接入面。",
      "Hotline 处理能力如何被产品化——它是契约层。一根 Hotline 可以暴露为 MCP Server，但 product / routing / settlement 由 Hotline 协议处理。",
    ],
    accent: "green",
  },
  {
    num: "Q2",
    question: "需要部署服务器吗？",
    bodyLines: [
      "不需要。Local Mode 可以在本地跑通完整链路：安装 CLI → 启动 supervisor → 挂载 Hotline → 发起 call → 看到 result_package。",
      "需要公网可达时，可以选择 Self-host Platform 或未来的 Public Marketplace。",
    ],
    accent: "yellow",
  },
  {
    num: "Q3",
    question: "开源协议是什么？",
    bodyLines: [
      "CALL ANYTHING 的协议、客户端运行时和自托管平台使用 Apache-2.0 开源。",
      "你可以本地运行、包装能力、自托管私有 catalog、基于协议做二次开发。",
      "官方 Marketplace、公共发现和未来结算网络属于官方运营层。",
    ],
    accent: "pink",
  },
];
